import {
  Checkbox,
  Drawer,
  Flex,
  Image,
  Popconfirm,
  Tooltip,
  Typography,
} from 'antd';
import {
  ActionButtonWrapper,
  Container,
  Description,
  ImageContainer,
  IssueName,
  RoutineSignImage,
  RoutineSigns,
} from './style';
import { Routine, SuggestionStatus, YesOrNo } from '../../types';
import { useTranslation } from 'react-i18next';
import Icon from '@ant-design/icons';
import { SVGDelete, SVGEdit } from '../../images';
import SuggestionTag from './suggestionTag';
import { useState } from 'react';
import { fetchIssues } from '../../slices/issues';
import { useAppDispatch } from '../../store';
import RoutineForm from '../routineForm';
import ReviewRoutineDrawer from '../reviewRoutineDrawer';
import {
  getIso7010SignImagePath,
  iso7010SignDescriptions,
  parseSignNames,
} from '../../constants';

export interface RoutineCardProps {
  routine: Routine;
  showEdit?: boolean;
  showDelete?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  checked?: boolean;
  checkable?: boolean;
  showSuggestionStatus?: boolean;
  onCheck?: (checked: boolean) => void;
}

const RoutineCard = ({
  routine,
  showEdit,
  showDelete,
  onEdit,
  onDelete,
  checkable,
  checked,
  onCheck,
  showSuggestionStatus,
}: RoutineCardProps) => {
  const { t } = useTranslation();
  const id = routine.routineID;
  const [suggestionReviewDrawerVisible, setSuggestionReviewDrawerVisible] =
    useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);

  const dispatch = useAppDispatch();

  const handleSuggestionReviewDrawerClose = (isSubmitted?: boolean) => {
    setSuggestionReviewDrawerVisible(false);
    if (isSubmitted) {
      dispatch(fetchIssues({ templateID: routine.templateID }));
    }
  };

  const handleClick = (e: any) => {
    e.stopPropagation();
    if (
      routine.isSuggestion === YesOrNo.YES &&
      routine.suggestionStatus === SuggestionStatus.SUBMITTED
    ) {
      setSuggestionReviewDrawerVisible(true);
    } else {
      setShowDetailsDrawer(true);
    }
  };

  return (
    <Flex flex={1} align="center" style={{ width: '100%' }}>
      <Container
        className={`${checked ? 'checked' : ''} ${checkable ? 'checkable' : ''}`}
        onClick={() => onCheck && onCheck(!checked)}
      >
        <Flex gap={8} onClick={handleClick}>
          {checkable && (
            <Checkbox
              checked={checked}
              onChange={(e) => onCheck && onCheck(e.target.checked)}
            ></Checkbox>
          )}
          <ImageContainer>
            <Image
              src={routine.images?.[0]?.url}
              preview={false}
              fallback="/placeholder.png"
            />
          </ImageContainer>
          <Flex vertical flex={1}>
            <IssueName>{routine.name}</IssueName>
            {!!routine.signNames && (
              <RoutineSigns>
                {parseSignNames(routine.signNames).map((code) => (
                  <Tooltip
                    key={code}
                    title={`${code} - ${iso7010SignDescriptions[code]}`}
                  >
                    <RoutineSignImage
                      src={getIso7010SignImagePath(code)}
                      alt={code}
                      title={code.toLowerCase()}
                    />
                  </Tooltip>
                ))}
              </RoutineSigns>
            )}
          </Flex>
          {showSuggestionStatus && routine.suggestionStatus && (
            <SuggestionTag status={routine.suggestionStatus} />
          )}
        </Flex>
        {routine.description && (
          <Tooltip title={routine.description.replace(/<[^>]*>/g, '')}>
            <Description>
              {routine.description.replace(/<[^>]*>/g, '')}
            </Description>
          </Tooltip>
        )}
        {showSuggestionStatus && (
          <Flex gap={16}>
            {routine.isSuggestion === YesOrNo.YES && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Submitted By: {routine?.suggestionSubmitterName || '-'} {'    '}{' '}
                at {routine?.suggestionSubmittedTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
            {routine.suggestionStatus === SuggestionStatus.APPROVED && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Accepted By: {routine?.suggestionReviewerName || '-'} {'    '}{' '}
                at {routine?.suggestionReviewTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
            {routine.suggestionStatus === SuggestionStatus.REJECTED && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Rejected By: {routine?.suggestionReviewerName || '-'} {'    '}{' '}
                at {routine?.suggestionReviewTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
          </Flex>
        )}
      </Container>
      <Flex gap={8}>
        {showEdit && (
          <ActionButtonWrapper
            onClick={(e) => onEdit?.(id)}
            style={{ cursor: 'pointer' }}
            title={t('edit')}
          >
            <Icon component={SVGEdit} />
          </ActionButtonWrapper>
        )}

        {showDelete && (
          <Popconfirm
            placement="bottom"
            title={`Are you sure you want to delete ${routine.name}`}
            okText={t('yes')}
            cancelText={t('no')}
            onConfirm={(e) => onDelete?.(id)}
          >
            <ActionButtonWrapper
              style={{ cursor: 'pointer' }}
              title={t('delete')}
            >
              <Icon component={SVGDelete} />
            </ActionButtonWrapper>
          </Popconfirm>
        )}
      </Flex>
      <ReviewRoutineDrawer
        visible={suggestionReviewDrawerVisible}
        onClose={handleSuggestionReviewDrawerClose}
        data={routine}
      />
      <Drawer
        title={`Routine-${routine?.name || ''}`}
        closable
        onClose={() => setShowDetailsDrawer(false)}
        open={showDetailsDrawer}
        width={800}
        footer={null}
      >
        <RoutineForm initialRoutine={routine} readOnly />
      </Drawer>
    </Flex>
  );
};

export default RoutineCard;
